import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DynamicIcon, Tooltip } from 'src/components';
import { useSelectedPartner } from 'src/graphql';
import {
  CustomViewFormConditionField,
  CustomViewFormConditionItem,
  CustomViewFormModel,
  UIType,
} from 'src/graphql/entities/custom-view';
import {
  isFlagField,
  isMulti,
  isUnary,
} from 'src/graphql/entities/custom-view/custom-view-config/operators';
import {
  mapValuesToOptions,
  resolveFieldOptions,
  useCustomViewFilterOptions,
} from 'src/graphql/entities/custom-view/custom-view-config/use-custom-view-options';
import { RulesDatePicker } from 'src/pages/Settings/rules/components/rules-date-picker';
import { capitalizeString } from 'src/utils/strings';
import {
  emptyValueFor,
  iconForUiType,
  isValueReady,
  operatorsForField,
  OptionItem,
} from './mobile-filter-wizard.helpers';
import { SortableConditionRow } from './sortable-condition-row';

type Step = 'overview' | 'field' | 'condition';

interface MobileFilterWizardProps {
  open: boolean;
  onClose: () => void;
  name: string;
  filterAttributesList: CustomViewFormConditionField[];
  onChange?: (values: CustomViewFormModel['conditions']) => void;
}

const EMPTY_CONDITIONS: CustomViewFormConditionItem[] = [];

const DEFAULT_TIMEZONE = 'America/New_York';

export const MobileFilterWizard = ({
  open,
  onClose,
  name,
  filterAttributesList,
  onChange,
}: MobileFilterWizardProps) => {
  const { control, setValue } = useFormContext();
  const { selectedPartner } = useSelectedPartner();

  const query = useWatch({ control, name }) as CustomViewFormModel['conditions'];

  const [step, setStep] = useState<Step>('overview');
  const [draft, setDraft] = useState<CustomViewFormConditionItem | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [attributeSearch, setAttributeSearch] = useState('');

  const rules = useMemo(() => query?.rules ?? [], [query]);

  const optionConditions = useMemo(
    () => (draft?.field ? [...rules, draft] : rules),
    [rules, draft],
  );
  const queryOptions = useCustomViewFilterOptions({
    partnerId: selectedPartner?.partnerId,
    conditions: open ? optionConditions : EMPTY_CONDITIONS,
  });

  const commit = (next: CustomViewFormModel['conditions'], notify = true) => {
    setValue(name, next, { shouldDirty: true, shouldValidate: true });
    if (notify) onChange?.(next);
  };

  const optionsFor = (field?: CustomViewFormConditionField) =>
    resolveFieldOptions(field, queryOptions) as OptionItem[];

  const resetWizard = () => {
    setStep('overview');
    setDraft(null);
    setEditingId(null);
    setAttributeSearch('');
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const handleCombinator = (combinator: string) =>
    commit({ ...query, combinator }, rules.length > 0);

  const handleDelete = (id?: string) =>
    commit({ ...query, rules: rules.filter((rule) => rule.id !== id) });

  const sensors = useSensors(useSensor(PointerSensor));

  const handleReorder = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rules.findIndex((rule) => rule.id === active.id);
    const newIndex = rules.findIndex((rule) => rule.id === over.id);
    commit({ ...query, rules: arrayMove(rules, oldIndex, newIndex) });
  };

  const startAdd = () => {
    setDraft({ id: crypto.randomUUID(), value: '' });
    setEditingId(null);
    setAttributeSearch('');
    setStep('field');
  };

  const startEdit = (rule: CustomViewFormConditionItem) => {
    setDraft({ ...rule });
    setEditingId(rule.id ?? null);
    setStep('condition');
  };

  const applyDraft = (candidate: CustomViewFormConditionItem) => {
    if (!candidate.field || !candidate.operator) return;
    const rule: CustomViewFormConditionItem = {
      id: candidate.id ?? crypto.randomUUID(),
      field: candidate.field,
      operator: candidate.operator,
      value: candidate.value,
    };
    const nextRules = editingId
      ? rules.map((existing) => (existing.id === editingId ? rule : existing))
      : [...rules, rule];
    commit({ ...query, rules: nextRules });
    resetWizard();
  };

  const pickField = (field: CustomViewFormConditionField) => {
    const [firstOperator] = operatorsForField(field);

    // flag fields have nothing to configure, so they are applied right away
    if (isFlagField(field)) {
      applyDraft({ ...draft, field, operator: firstOperator?.value, value: '' });
      return;
    }

    setDraft((current) => ({
      ...current,
      field,
      operator: firstOperator?.value,
      value: emptyValueFor(field, !!firstOperator?.multi),
    }));
    setStep('condition');
  };

  const changeOperator = (value: CustomViewFormConditionItem['operator']) =>
    setDraft((current) => {
      if (!current) return current;
      const next = operatorsForField(current.field).find((o) => o.value === value);
      const sameShape = !isUnary(value) && !!next?.multi === isMulti(current.operator);
      return {
        ...current,
        operator: value,
        value: sameShape ? current.value : emptyValueFor(current.field, !!next?.multi),
      };
    });

  const setDraftValue = (value: CustomViewFormConditionItem['value']) =>
    setDraft((current) => ({ ...current!, value }));

  const groupedAttributes = useMemo(() => {
    const search = attributeSearch.trim().toLowerCase();
    const matches = filterAttributesList.filter((field) =>
      field.label.toLowerCase().includes(search),
    );
    return matches.reduce<Record<string, CustomViewFormConditionField[]>>((groups, field) => {
      const key = field.group || 'Other';
      (groups[key] ??= []).push(field);
      return groups;
    }, {});
  }, [attributeSearch, filterAttributesList]);

  const renderHeader = (title: string, onBack?: () => void) => (
    <>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        padding="1rem"
      >
        <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
          {onBack ? (
            <IconButton onClick={onBack} sx={{ ml: '-0.5rem' }}>
              <DynamicIcon name="arrow_back" />
            </IconButton>
          ) : null}
          <Typography variant="body2" noWrap>
            {title}
          </Typography>
        </Stack>
        <Tooltip title="close">
          <IconButton onClick={handleClose}>
            <DynamicIcon name="close" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
    </>
  );

  const renderProgress = (current: number) => (
    <LinearProgress variant="determinate" value={(current / 2) * 100} sx={{ height: 3 }} />
  );

  const renderSectionHeader = (label: string) => (
    <Box
      padding="1rem"
      sx={{ backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh }}
    >
      <Typography variant="body2">{label}</Typography>
    </Box>
  );

  const renderNavRow = ({
    key,
    label,
    onClick,
    icon,
  }: {
    key: string;
    label: string;
    onClick: () => void;
    icon?: string;
  }) => (
    <Stack
      key={key}
      direction="row"
      alignItems="center"
      gap={1.25}
      onClick={onClick}
      sx={{
        padding: '0.625rem 1rem',
        cursor: 'pointer',
        borderBottom: (theme) => `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
        '&:last-of-type': { borderBottom: 'none' },
        '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
      }}
    >
      {icon ? (
        <DynamicIcon
          name={icon}
          sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant }}
        />
      ) : null}
      <Tooltip title={label}>
        <Typography variant="body2" flex={1} minWidth={0} noWrap>
          {label}
        </Typography>
      </Tooltip>
    </Stack>
  );

  const renderValueControl = () => {
    if (!draft?.field || !draft.operator) return null;
    const { field } = draft;

    if (field.uiType === UIType.List) {
      const optionsData = field.optionsQuery ? queryOptions[field.optionsQuery] : undefined;
      const options = (optionsData?.options ?? field.fieldOptions ?? []) as OptionItem[];
      const multiple = isMulti(draft.operator);
      const known = optionsFor(field);
      const raw = draft.value ?? (multiple ? [] : null);
      const value = (Array.isArray(raw) ? mapValuesToOptions(raw, known) : raw) as
        | OptionItem
        | OptionItem[]
        | null;
      return (
        <Autocomplete
          multiple={multiple}
          disableClearable={!multiple}
          openOnFocus
          options={options}
          value={value as never}
          loading={Boolean(optionsData?.isLoading)}
          getOptionLabel={(option) => option?.label ?? ''}
          getOptionKey={(option) => String(option?.id ?? '')}
          isOptionEqualToValue={(option, selected) => option?.value === selected?.value}
          onInputChange={(_, input, reason) => {
            if (reason === 'input') optionsData?.onInputChange?.(input);
          }}
          onChange={(_, next) => setDraftValue(next as CustomViewFormConditionItem['value'])}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder={`Search ${field.pluralLabel ?? 'options'}…`}
            />
          )}
        />
      );
    }

    if (field.uiType === UIType.Date) {
      return (
        <RulesDatePicker
          value={(draft.value as string) ?? ''}
          timezone={selectedPartner?.timeZone ?? DEFAULT_TIMEZONE}
          onChange={({ isRollingDate, value }) =>
            setDraftValue(isRollingDate ? (value as string) : (value as Dayjs).toISOString())
          }
        />
      );
    }

    return (
      <TextField
        fullWidth
        autoFocus
        type={field.uiType === UIType.Number ? 'number' : 'text'}
        value={typeof draft.value === 'string' ? draft.value : ''}
        placeholder="Enter a value"
        onChange={(event) => setDraftValue(event.target.value)}
      />
    );
  };

  const renderOverview = () => (
    <>
      {renderHeader('SET FILTERS')}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {renderSectionHeader('Match')}
        <Box padding="1rem">
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={query?.combinator ?? 'and'}
            onChange={(_, next: string | null) => {
              if (next) handleCombinator(next);
            }}
          >
            <ToggleButton value="and">AND</ToggleButton>
            <ToggleButton value="or">OR</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {renderSectionHeader('Conditions')}
        {rules.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleReorder}
          >
            <SortableContext
              items={rules.map((rule) => rule.id!)}
              strategy={verticalListSortingStrategy}
            >
              <Stack padding="1rem" gap={0.75}>
                {rules.map((rule, index) => (
                  <SortableConditionRow
                    key={rule.id}
                    rule={rule}
                    index={index}
                    combinator={query?.combinator}
                    options={optionsFor(rule.field)}
                    onEdit={startEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ padding: '1rem' }}>
            No filters yet. Add one to narrow down results.
          </Typography>
        )}

        <Box padding="1rem">
          <Button fullWidth startIcon={<DynamicIcon name="add" />} onClick={startAdd}>
            Add filter
          </Button>
        </Box>
      </Box>
      <Divider />
      <Stack direction="row" padding="1rem">
        <Button fullWidth variant="contained" onClick={handleClose}>
          Save
        </Button>
      </Stack>
    </>
  );

  const renderFieldStep = () => (
    <>
      {renderHeader('Add filter', () => setStep('overview'))}
      {renderProgress(1)}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Box padding="1rem">
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Search attributes"
            value={attributeSearch}
            onChange={(event) => setAttributeSearch(event.target.value)}
            InputProps={{
              startAdornment: <DynamicIcon name="search" sx={{ marginRight: '0.5rem' }} />,
            }}
          />
        </Box>
        {Object.entries(groupedAttributes).map(([group, fields]) => (
          <Stack key={group}>
            {renderSectionHeader(capitalizeString(group))}
            {fields.map((field) =>
              renderNavRow({
                key: field.value,
                label: field.label,
                onClick: () => pickField(field),
                icon: iconForUiType(field.uiType),
              }),
            )}
          </Stack>
        ))}
      </Box>
    </>
  );

  const renderConditionStep = () => (
    <>
      {renderHeader('Add filter', () => setStep('field'))}
      {renderProgress(2)}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {renderSectionHeader(draft?.field?.label ?? '')}
        <Box padding="1rem">
          <TextField
            select
            fullWidth
            label="Condition"
            value={draft?.operator ?? ''}
            onChange={(event) =>
              changeOperator(event.target.value as CustomViewFormConditionItem['operator'])
            }
          >
            {operatorsForField(draft?.field).map((operator) => (
              <MenuItem key={operator.value} value={operator.value}>
                {capitalizeString(operator.display)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {isUnary(draft?.operator) ? null : (
          <>
            <Divider />
            <Box padding="1rem">{renderValueControl()}</Box>
          </>
        )}
      </Box>
      <Divider />
      <Stack direction="row" padding="1rem">
        <Button
          fullWidth
          variant="contained"
          disabled={!isValueReady(draft)}
          onClick={() => applyDraft(draft!)}
        >
          Apply filter
        </Button>
      </Stack>
    </>
  );

  return (
    <Popover
      open={open}
      onClose={handleClose}
      anchorReference="none"
      slotProps={{
        paper: {
          sx: {
            width: '100vw',
            height: '100dvh',
            maxWidth: '100vw',
            maxHeight: '100dvh',
            borderRadius: 0,
            top: 0,
            left: 0,
          },
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        {step === 'overview' && renderOverview()}
        {step === 'field' && renderFieldStep()}
        {step === 'condition' && renderConditionStep()}
      </Stack>
    </Popover>
  );
};
